import { cssVar, Flex, Box, Icon, Text } from '@cad-arc';
import { CliArgumentEnumType, CliAutocompleteOption, CliResource } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useApiClient } from '@m43/core';
import { CommandLineEditorStore } from './command-line-editor-store';
import { LocationSearchHelper } from './location-search-helper';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { HelpWindow } from './help-window';
import { matchSorter } from 'match-sorter';
import { HospitalStatusTag } from '../common/components/hospital-status-tag';
import { WorkspacePanel } from '../panels/workspace-panel';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useLocalFirstEventAutocomplete } from '../event-autocomplete/use-local-first-event-autocomplete';
import {
    advanceEventAutocompleteSelectionMemo,
    EventAutocompleteSelectionMemo,
    findIndexByEventId,
    INITIAL_EVENT_AUTOCOMPLETE_SELECTION_MEMO,
    resolveEventAutocompleteSelectedId,
} from '../event-autocomplete/local-first-event-autocomplete';
import {
    eventQueueViewToCliAutocompleteOption,
    getCliAutocompleteOptionEventId,
} from './cli-event-autocomplete-option';

const AUTOCOMPLETE_BORDER_WIDTH = 2;
const AUTOCOMPLETE_ITEM_HEIGHT = 36;
const AUTOCOMPLETE_MAX_HEIGHT = AUTOCOMPLETE_ITEM_HEIGHT * 4;

interface AutocompleteOptionRowProps {
    onClick: () => void;
    onMouseEnter: () => void;
    item: CliAutocompleteOption;
    isSelected: boolean;
    isHospital: boolean;
}

function AutocompleteSubtitle({
    item,
    isHospital,
}: {
    item: CliAutocompleteOption;
    isHospital: boolean;
}) {
    if (!item.subtitle) {
        return null;
    }

    return (
        <>
            {isHospital ? (
                <HospitalStatusTag
                    hospitalStatusGlobalAttributeId={item.globalAttributeId}
                    hospitalStatus={item.subtitle}
                />
            ) : (
                <Text variant="headingSm" color="inherit" noOfLines={2}>
                    {item.subtitle}
                </Text>
            )}
        </>
    );
}

export function AutocompleteOptionRow({
    onClick,
    onMouseEnter,
    item,
    isSelected,
    isHospital,
}: AutocompleteOptionRowProps): React.ReactElement {
    return (
        <Flex
            direction="row"
            px="4"
            alignItems="center"
            h={AUTOCOMPLETE_ITEM_HEIGHT}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            cursor="pointer"
            w="full"
            bg={isSelected ? 'selected.default' : 'surface.canvas'}
            color={isSelected ? 'selected.content' : 'text.primary'}
        >
            <Box
                flex={2}
                style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
            >
                <Text
                    color="inherit"
                    style={{
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                    variant="headingSm"
                >
                    {item.title}
                </Text>
            </Box>

            <Box flex={3}>
                <AutocompleteSubtitle item={item} isHospital={isHospital} />
            </Box>

            {item.secondarySubtitle ? (
                <Box flex={2}>
                    <Text color="inherit" variant="headingSm">
                        {item.secondarySubtitle}
                    </Text>
                </Box>
            ) : null}

            <Flex
                title="Cleared event"
                w="20px"
                flexShrink={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                {item.eventDetails?.isActive === false ? (
                    <Icon
                        icon="Closed"
                        aria-label="Cleared event"
                        color={isSelected ? 'brand.content' : 'icon.default'}
                    />
                ) : null}
            </Flex>
        </Flex>
    );
}

export const Autocomplete = observer(function Autocomplete(props: {
    editorStore: CommandLineEditorStore;
}): React.ReactElement {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const { editorStore } = props;
    const apiClient = useApiClient();
    const localFirstEventAutocompleteEnabled =
        useEnabledFeatures().localFirstEventAutocompleteEnabled;
    const localFirstEventAutocomplete = useLocalFirstEventAutocomplete({
        isEnabled: localFirstEventAutocompleteEnabled,
        mapEventQueueViewToOption: eventQueueViewToCliAutocompleteOption,
        getEventId: getCliAutocompleteOptionEventId,
    });
    const selectionMemoRef = useRef<EventAutocompleteSelectionMemo>(
        INITIAL_EVENT_AUTOCOMPLETE_SELECTION_MEMO
    );

    const query = editorStore.helperSearchText;
    const segmentType = editorStore.getCurrentSegment?.segmentType;
    const selectedIndex = editorStore.helperState?.selectedIndex;

    const rows = editorStore.helperState?.options ?? [];

    const totalCount = rows.length;

    const enabled =
        !!segmentType &&
        !!editorStore.helperState?.activeHelper &&
        editorStore.helperState.activeHelper !== 'HELP' &&
        editorStore.helperState.activeHelper === segmentType;

    useEffect(() => {
        editorStore.isAutocompleteLoading = enabled;
    }, [editorStore, enabled]);

    // Apply a freshly-computed logical (best-first) options list for `query`. Derives the
    // display (reversed) options and the selectedIndex from whatever is *currently* selected
    // in the store, looked up by event id — never by remembering + offsetting a previous
    // index — so a reconcile can never desync from an in-flight user navigation or an
    // unexpectedly-shaped array. See resolveEventAutocompleteSelectedId for the invariant.
    const applyEventOptions = useCallback(
        (forQuery: string, logicalOptions: CliAutocompleteOption[]) => {
            const displayOptions = [...logicalOptions].reverse();
            const selectedId = resolveEventAutocompleteSelectedId({
                memo: selectionMemoRef.current,
                query: forQuery,
                previousOptions: editorStore.helperState?.options ?? [],
                previousSelectedIndex: editorStore.helperState?.selectedIndex,
                newOptions: logicalOptions,
                getEventId: getCliAutocompleteOptionEventId,
            });
            editorStore.setHelperOptions(displayOptions);
            editorStore.setHelperSelectedIndex(
                findIndexByEventId(displayOptions, selectedId, getCliAutocompleteOptionEventId)
            );
            selectionMemoRef.current = advanceEventAutocompleteSelectionMemo(
                selectionMemoRef.current,
                forQuery,
                logicalOptions.length > 0
            );
        },
        [editorStore]
    );

    // Local-first: paint active-event matches from the client's live event-queue-views map
    // immediately, before the server responds. Runs per keystroke; the server useQuery below
    // reconciles the results in an append-only way. Safe to re-fire redundantly for the same
    // query — applyEventOptions re-derives the current selection by identity, so it's a no-op
    // when nothing has actually changed.
    useEffect(() => {
        // Feature-flag gate first, as a pure no-op: when local-first is off this effect must
        // do nothing at all (not even touch internal refs), so behavior is byte-for-byte the
        // pre-feature command line.
        if (!localFirstEventAutocompleteEnabled) {
            return;
        }
        if (!enabled || segmentType !== 'EVENT' || !query) {
            // Autocomplete isn't active for an event query (helper closed, different segment,
            // or the query was cleared). Reset the "selection already locked" memo so that
            // re-entering the SAME query text later is treated as a fresh query — selection
            // snaps back to the first result rather than resuming the prior session's
            // (possibly user-navigated) selection. Without this, clearing the input and
            // retyping the identical string leaves memo.query unchanged and the reconcile
            // path keeps the stale selection.
            selectionMemoRef.current = INITIAL_EVENT_AUTOCOMPLETE_SELECTION_MEMO;
            return;
        }
        // Paint the instant client set once per query, on the first run where the query
        // becomes active. `memo.query` only equals `query` after we've already processed a
        // result set for it, so this skips any later re-fire of the effect for the same
        // query. That matters because the server reconcile (in onSuccess) replaces the store
        // options with the merged client+server list; repainting the client-only set here
        // would silently drop the appended server/cleared rows. Gating on the query makes the
        // paint idempotent by construction instead of relying on this effect's dependency
        // array never re-firing mid-query.
        if (selectionMemoRef.current.query === query) {
            return;
        }
        applyEventOptions(query, localFirstEventAutocomplete.getClientOptions(query));
    }, [
        localFirstEventAutocompleteEnabled,
        localFirstEventAutocomplete,
        enabled,
        segmentType,
        query,
        applyEventOptions,
    ]);

    useQuery(
        ['command-line-search', segmentType, query],
        ({ queryKey }) => {
            editorStore.isAutocompleteLoading = true;

            return apiClient
                .request<CliResource.Autocomplete>({
                    method: 'POST',
                    url: `cli/autocomplete`,
                    // Intentionally supressing errors here to maintain existing behavior/logic.
                    // Down the line we'll want to conform to some error handling logic but it will
                    // require additional design and product work - NH
                    shouldFailSilently: true,
                    data: {
                        cliArgument: queryKey[1] as CliArgumentEnumType,
                        cliCommand: editorStore.activeCommandSegment?.segmentData
                            .cliArgument as CliArgumentEnumType,
                        query: queryKey[2] ?? '',
                    },
                })
                .then((resp) => {
                    return resp.data.data?.cliAutocompleteOptions;
                });
        },
        {
            enabled,
            onSuccess(cliAutocompleteOptions) {
                if (!cliAutocompleteOptions) {
                    return;
                }
                if (localFirstEventAutocompleteEnabled && segmentType === 'EVENT') {
                    // Stale-response guard: a slow response for an older query must never
                    // overwrite the results of a newer one. `query` here is closed over from
                    // the render that issued this request; compare against the live query.
                    if (query !== editorStore.helperSearchText) {
                        return;
                    }
                    const clientOptions = localFirstEventAutocomplete.getClientOptions(query ?? '');
                    const mergedOptions = localFirstEventAutocomplete.reconcile(
                        clientOptions,
                        cliAutocompleteOptions
                    );
                    applyEventOptions(query ?? '', mergedOptions);
                    return;
                }
                editorStore.setHelperOptions(getSortedOptions(cliAutocompleteOptions).reverse());
            },
            onSettled() {
                editorStore.isAutocompleteLoading = false;
            },
        }
    );

    const getSortedOptions = (cliAutocompleteOptions: CliAutocompleteOption[]) => {
        if (segmentType === 'EVENT') {
            // events have a specific order where entries ending with the query goes first, BE do this sorting
            return cliAutocompleteOptions;
        }
        return matchSorter(cliAutocompleteOptions, query ?? '', {
            keys: ['title', 'subtitle', 'secondarySubtitle'],
            baseSort: (a, b) => a.item.title.localeCompare(b.item.title),
            threshold: matchSorter.rankings.NO_MATCH,
        });
    };

    const renderItemContent = (index: number): React.ReactElement => {
        const item = rows[index];

        return (
            <AutocompleteOptionRow
                item={item}
                onClick={() => {
                    editorStore.externalSelectOption(item);
                }}
                onMouseEnter={() => editorStore.setHelperSelectedIndex(index)}
                isSelected={index === selectedIndex}
                isHospital={editorStore.helperState?.activeHelper === 'HOSPITAL'}
            />
        );
    };

    const innerHeight = totalCount * AUTOCOMPLETE_ITEM_HEIGHT;
    const listHeight =
        (innerHeight > AUTOCOMPLETE_MAX_HEIGHT ? AUTOCOMPLETE_MAX_HEIGHT : innerHeight) +
        AUTOCOMPLETE_BORDER_WIDTH * 2;

    useEffect(() => {
        setTimeout(() => {
            if (typeof selectedIndex !== 'undefined') {
                virtuosoRef.current?.scrollIntoView({ index: selectedIndex, behavior: 'auto' });
            }
        }, 0);
    }, [selectedIndex, virtuosoRef]);

    if (!totalCount) {
        return <></>;
    }

    return (
        <Flex w="100%" justifyContent="center">
            <Virtuoso
                style={{
                    borderRadius: 'var(--arc-radii-base)',
                    borderWidth: AUTOCOMPLETE_BORDER_WIDTH + 'px',
                    borderStyle: 'solid',
                    borderColor: 'var(--cad-colors-command-line-popout-border)',
                    backgroundColor: cssVar('arc.colors.surface.canvas'),
                    display: 'block',
                    height: listHeight,
                    width: '75%',
                    minWidth: '250px',
                }}
                ref={virtuosoRef}
                totalCount={totalCount}
                overscan={10}
                itemContent={renderItemContent}
                initialTopMostItemIndex={totalCount - 1}
            />
        </Flex>
    );
});

export const CommandLineHelpers = observer(function CommandLineHelpers(props: {
    editorStore: CommandLineEditorStore;
    locationApi: ReturnType<typeof useLocationApi>;
    historyHeight: number;
    panel: WorkspacePanel;
}): React.ReactElement {
    const { editorStore, historyHeight } = props;

    if (editorStore.helperState?.activeHelper === 'LOCATION') {
        return <LocationSearchHelper locationApi={props.locationApi} editorStore={editorStore} />;
    }

    if (editorStore.helperState?.activeHelper === 'HELP') {
        return <HelpWindow historyHeight={historyHeight} editorStore={editorStore} />;
    }
    return <Autocomplete editorStore={editorStore} />;
});
